/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { ResponsiveTable, type ResponsiveTableColumn } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useQuery } from "@tanstack/vue-query";
import { CatalogSheet } from "./CatalogSheet";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

type MarketplaceRestaurant = {
  id: number;
  name: string;
  slug: string | null;
  status: string;
  address: string | null;
  description: string | null;
  currency: string | null;
  timezone: string | null;
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

export const BrowsePage = defineComponent({
  name: "BrowsePage",
  setup() {
    const trpc = useTrpc();
    const search = ref("");
    const selectedRestaurant = ref<MarketplaceRestaurant | null>(null);
    const isSheetOpen = ref(false);

    const restaurantsQuery = useQuery({
      queryKey: ["marketplace-restaurants"],
      queryFn: () => trpc.marketplace.listRestaurants.query(),
      staleTime: 60_000,
    });

    const filteredRestaurants = computed(() => {
      const list = restaurantsQuery.data.value ?? [];
      const q = search.value.trim().toLowerCase();
      if (!q) return list;
      return list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.address ?? "").toLowerCase().includes(q) ||
          (r.description ?? "").toLowerCase().includes(q),
      );
    });

    const openCatalog = (restaurant: MarketplaceRestaurant) => {
      selectedRestaurant.value = restaurant;
      isSheetOpen.value = true;
    };

    const closeSheet = () => {
      isSheetOpen.value = false;
    };

    const columns: ResponsiveTableColumn[] = [
      {
        key: "name",
        label: "Supplier",
        mobilePriority: "primary",
        render: (row: MarketplaceRestaurant) => (
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span class="text-xs font-bold text-primary">
                {getInitials(row.name)}
              </span>
            </div>
            <div class="min-w-0">
              <p class="font-semibold text-sm text-base-content truncate">
                {row.name}
              </p>
              {row.description && (
                <p class="text-xs text-base-content/50 truncate max-w-xs">
                  {row.description}
                </p>
              )}
            </div>
          </div>
        ),
      },
      {
        key: "address",
        label: "Location",
        mobilePriority: "secondary",
        render: (row: MarketplaceRestaurant) =>
          row.address ? (
            <span class="flex items-center gap-1 text-sm text-base-content/70">
              <span class="material-symbols-outlined text-sm text-base-content/40">
                location_on
              </span>
              {row.address}
            </span>
          ) : (
            <span class="text-base-content/30 text-sm">—</span>
          ),
      },
      {
        key: "currency",
        label: "Currency",
        mobilePriority: "secondary",
        render: (row: MarketplaceRestaurant) =>
          row.currency ? (
            <span class="badge badge-ghost badge-sm font-mono font-semibold">
              {row.currency}
            </span>
          ) : (
            <span class="text-base-content/30 text-sm">—</span>
          ),
      },
      {
        key: "status",
        label: "Status",
        mobilePriority: "secondary",
        render: (row: MarketplaceRestaurant) => (
          <span
            class={`badge badge-sm gap-1 ${
              row.status === "active"
                ? "badge-success/15 text-success"
                : "badge-ghost text-base-content/40"
            }`}
          >
            <span class="material-symbols-outlined text-xs">
              {row.status === "active"
                ? "check_circle"
                : "radio_button_unchecked"}
            </span>
            {row.status === "active" ? "Active" : row.status}
          </span>
        ),
      },
      {
        key: "action",
        label: "",
        mobilePriority: "actions",
        render: (row: MarketplaceRestaurant) => (
          <button
            class="btn  btn-primary gap-1.5 border border-base-content/10 hover:btn-primary hover:border-transparent transition-colors"
            onClick={(e: Event) => {
              e.stopPropagation();
              openCatalog(row);
            }}
          >
            <span class="material-symbols-outlined text-2xl">
              shopping_cart
            </span>
          </button>
        ),
      },
    ];

    const renderStats = () => {
      const total = restaurantsQuery.data.value?.length ?? 0;
      return (
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <div class="rounded-xl border border-base-content/8 bg-base-100 p-4 flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <span class="material-symbols-outlined text-primary">
                storefront
              </span>
            </div>
            <div>
              <p class="text-2xl font-bold text-base-content tabular-nums">
                {restaurantsQuery.isLoading.value ? (
                  <span class="loading loading-xs loading-dots" />
                ) : (
                  total
                )}
              </p>
              <p class="text-xs text-base-content/50 font-medium">
                Active suppliers
              </p>
            </div>
          </div>

          <div class="rounded-xl border border-base-content/8 bg-base-100 p-4 flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <span class="material-symbols-outlined text-success">
                inventory_2
              </span>
            </div>
            <div>
              <p class="text-2xl font-bold text-base-content">—</p>
              <p class="text-xs text-base-content/50 font-medium">
                Products available
              </p>
            </div>
          </div>

          <div class="hidden sm:flex rounded-xl border border-base-content/8 bg-base-100 p-4 items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <span class="material-symbols-outlined text-warning">
                shopping_bag
              </span>
            </div>
            <div>
              <p class="text-2xl font-bold text-base-content">—</p>
              <p class="text-xs text-base-content/50 font-medium">
                Orders placed
              </p>
            </div>
          </div>
        </div>
      );
    };

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title="Browse Suppliers"
          subtitle="Discover and order from partner restaurants and suppliers"
        >
          <div class="relative w-full sm:w-72">
            <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-base-content/35 text-lg pointer-events-none">
              search
            </span>
            <input
              type="text"
              placeholder="Search suppliers…"
              value={search.value}
              onInput={(e: Event) => {
                search.value = (e.target as HTMLInputElement).value;
              }}
              class="input input-bordered input-sm w-full pl-9 bg-base-100"
            />
          </div>
        </SubpageHeader>

        <div class="flex-1 overflow-auto p-4">
          {renderStats()}

          {/* Suppliers table */}
          <div class="rounded-xl border border-base-content/8 bg-base-100 overflow-hidden">
            <div class="flex items-center justify-between px-4 py-3 border-b border-base-content/8 bg-base-200/30">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-base-content/50 text-base">
                  list
                </span>
                <span class="text-sm font-semibold text-base-content/70">
                  All Suppliers
                  {!restaurantsQuery.isLoading.value && (
                    <span class="ml-1.5 badge badge-ghost badge-xs font-mono">
                      {filteredRestaurants.value.length}
                    </span>
                  )}
                </span>
              </div>
            </div>

            <ResponsiveTable
              columns={columns}
              rows={filteredRestaurants.value}
              rowKey={(r) => r.id}
              onRowClick={openCatalog}
              loading={restaurantsQuery.isLoading.value}
              emptyMessage={
                search.value
                  ? `No suppliers match "${search.value}"`
                  : "No suppliers available in the marketplace yet"
              }
            />
          </div>
        </div>

        {/* Catalog RightSheet */}
        <CatalogSheet
          restaurant={selectedRestaurant.value}
          isOpen={isSheetOpen.value}
          onClose={closeSheet}
        />
      </div>
    );
  },
});
